('use client');

import { FC, useCallback, useEffect, useState } from 'react';
import { useLoggedInUser } from '@shared/Authentication/shared/infrastructure/store/auth.context';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useAnalyticsTracker } from '@services/analytics';
import { UnhandledRenderingErrorRaisedEvent } from '../model/unhandled-rendering-error-raised.event';
import { ReportableError } from '../model/reportable-error';
import { AptunoButton, Modal, SIZES, VARIANTS } from '@aptuno/aptuno-ui';
import { FallbackModalStyle } from './ErrorReporter.styled';
import { useReportError } from '../api/report-error';

const ErrorFallback: FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const tracker = useAnalyticsTracker();
  const reportError = useReportError();
  const loggedInUser = useLoggedInUser();

  const [openView, setOpenView] = useState<boolean>(false);

  const render = useCallback(() => {
    setOpenView(true);
  }, []);
  const unrender = useCallback(() => {
    resetErrorBoundary();
    setOpenView(false);
  }, [resetErrorBoundary]);

  useEffect(() => {
    if (error) {
      const reportableError = ReportableError.of({
        error,
        loggedInUser: loggedInUser?.data,
      });

      const event = UnhandledRenderingErrorRaisedEvent.of(reportableError);

      Promise.allSettled([tracker.trackEvent(event), reportError(reportableError)]).finally(() => {
        render();
      });
    }
  }, [error]);

  return (
    <Modal
      header={
        <FallbackModalStyle.Header>
          <FallbackModalStyle.Title>¡Parece que algo no salió muy bien!</FallbackModalStyle.Title>
        </FallbackModalStyle.Header>
      }
      size={500}
      isActive={openView}
      onClose={unrender}
    >
      <FallbackModalStyle.Content>
        <ul style={{ paddingBottom: 20 }}>
          <li>
            <FallbackModalStyle.Paragrah>
              Nuestro equipo fue informado y resolverá en breve.
            </FallbackModalStyle.Paragrah>
          </li>
          <li>
            <FallbackModalStyle.Paragrah>
              Nos pondremos en contacto contigo de ser necesario.
            </FallbackModalStyle.Paragrah>
          </li>
        </ul>
        <AptunoButton appearance={VARIANTS.PRIMARY} size={SIZES.BIGGEST} onClick={unrender}>
          Volver
        </AptunoButton>
      </FallbackModalStyle.Content>
    </Modal>
  );
};

const WithErrorReporter = ({ children }): JSX.Element => {
  return <ErrorBoundary FallbackComponent={ErrorFallback}>{children}</ErrorBoundary>;
};

export default WithErrorReporter;
